import { normalizeRoleToKey } from "../../Enums/UserEnums";

/**
 * Input the guard reads. Views should not inspect these fields; they ask
 * `userHasRole` / `userHasPermission`. `roles` and `permissions` are reserved for when
 * the session carries more than a single role.
 */
export type GuardUser = {
  role?: string;
  roles?: string[];
  permissions?: string[];
};

function toArray(value: string | string[]): string[] {
  return Array.isArray(value) ? value : [value];
}

/**
 * Today's session has one `role`. Prefer `roles` when that list exists so
 * callers of `userHasRole` do not change.
 */
function resolveRoles(user: GuardUser | null): string[] {
  if (!user) {
    return [];
  }
  if (user.roles && user.roles.length > 0) {
    return user.roles;
  }
  return user.role ? [user.role] : [];
}

function resolvePermissions(user: GuardUser | null): string[] {
  if (!user) {
    return [];
  }
  return user.permissions ?? [];
}


/**
 * True if the user has any of the given roles.
 * JWT B2C labels and Person role keys compare equal via `normalizeRoleToKey`.
 */
export function userHasRole(user: GuardUser | null, role: string | string[]): boolean {
  const owned = resolveRoles(user)
    .map((item) => normalizeRoleToKey(item))
    .filter(Boolean);

  if (owned.length === 0) {
    return false;
  }
  const wanted = toArray(role).map((item) => normalizeRoleToKey(item));
  return wanted.some((item) => owned.includes(item));
}

/**
 * True if the user has every given permission.
 * Permissions are not on the session yet; deny until that list exists.
 */
export function userHasPermission(user: GuardUser | null, permission: string | string[]): boolean {
  const granted = resolvePermissions(user);
  const wanted = toArray(permission);

  if (granted.length === 0) {
    return false;
  }
  return wanted.every((item) => granted.includes(item));
}

export function userIsDev(user: GuardUser | null): boolean {
  if (!user) {
    return false;
  }
  const isDev = (user as { isDev?: unknown }).isDev === true;
  return isDev || userHasRole(user, "dev");
}

export function userIs(user: GuardUser | null, type: string): boolean {
  if (!user) {
    return false;
  }
  const attribute = "is" + type.charAt(0).toUpperCase() + type.slice(1);
  const is = (user as { [key: string]: unknown })[attribute] === true;
  return is || userHasRole(user, type);
}